import type { ReactiveController, ReactiveControllerHost } from 'lit';

type KeyboardHost = ReactiveControllerHost &
  EventTarget & { shadowRoot: ShadowRoot | null; disabled?: boolean };

/**
 * Manages roving tabindex for the `asc-table` grid per the ARIA Data Grid pattern.
 *
 * - Exactly one cell (`<th>` or `<td>` with a `part` attribute) has `tabindex="0"`;
 *   all others have `tabindex="-1"`.
 * - Arrow keys, Home, End, Ctrl+Home, Ctrl+End move focus between cells.
 * - Skeleton rows (`aria-hidden="true"`) and empty-state rows are excluded.
 *
 * Co-located with `asc-table` — this controller has no value outside of it.
 */
export class TableKeyboardController implements ReactiveController {
  private readonly _host: KeyboardHost;
  private _table: HTMLTableElement | null = null;

  constructor(host: KeyboardHost) {
    this._host = host;
    host.addController(this);
  }

  /** Attach the controller to the rendered `<table>` element after `firstUpdated`. */
  setTable(table: HTMLTableElement | null): void {
    if (this._table) {
      this._table.removeEventListener('keydown', this._onKeyDown);
      this._table.removeEventListener('focusin', this._onFocusIn);
    }
    this._table = table;
    if (table) {
      table.addEventListener('keydown', this._onKeyDown);
      table.addEventListener('focusin', this._onFocusIn);
      this._initTabIndex();
    }
  }

  hostConnected(): void {}

  hostDisconnected(): void {
    this.setTable(null);
  }

  /** Reset the grid so that the first cell is the single tabbable entry point. */
  resetFocus(): void {
    this._initTabIndex();
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  /** Give `tabindex="0"` to the first cell, `-1` to all others. */
  private _initTabIndex(): void {
    const grid = this._getGrid();
    grid.forEach((cells, r) =>
      cells.forEach((cell, c) => {
        cell.tabIndex = r === 0 && c === 0 ? 0 : -1;
      }),
    );
  }

  /**
   * Build a 2-D array of focusable cells: row 0 = column-header row, rows 1…n = data rows.
   * Rows marked `aria-hidden="true"` (skeleton rows) are excluded.
   */
  private _getGrid(): HTMLElement[][] {
    if (!this._table) return [];
    const grid: HTMLElement[][] = [];
    const allRows = this._table.querySelectorAll<HTMLElement>(
      '[part="header-row"], [part="row"]',
    );
    allRows.forEach(row => {
      // Skip skeleton and empty-state rows
      if (row.getAttribute('aria-hidden') === 'true') return;
      const cells = Array.from(
        row.querySelectorAll<HTMLElement>(
          '[part="checkbox-col-header"], [part="col-header"], [part="checkbox-cell"], [part="cell"]',
        ),
      );
      if (cells.length) grid.push(cells);
    });
    return grid;
  }

  /** Find the grid [row, col] position for any focused DOM element. */
  private _findPos(target: EventTarget | null): { row: number; col: number } | null {
    if (!(target instanceof HTMLElement)) return null;
    const grid = this._getGrid();
    for (let r = 0; r < grid.length; r++) {
      for (let c = 0; c < grid[r].length; c++) {
        const cell = grid[r][c];
        if (cell === target || cell.contains(target)) return { row: r, col: c };
      }
    }
    return null;
  }

  /** Move keyboard focus to cell [row, col], clamped to grid bounds. */
  private _moveFocus(row: number, col: number): void {
    const grid = this._getGrid();
    if (!grid.length) return;
    row = Math.max(0, Math.min(grid.length - 1, row));
    col = Math.max(0, Math.min((grid[row]?.length ?? 1) - 1, col));

    grid.forEach((cells, r) =>
      cells.forEach((cell, c) => {
        cell.tabIndex = r === row && c === col ? 0 : -1;
      }),
    );
    grid[row]?.[col]?.focus();
  }

  /** When any cell receives focus track its position (supports click-to-focus). */
  private readonly _onFocusIn = (e: FocusEvent): void => {
    const pos = this._findPos(e.target);
    if (!pos) return;
    const grid = this._getGrid();
    grid.forEach((cells, r) =>
      cells.forEach((cell, c) => {
        cell.tabIndex = r === pos.row && c === pos.col ? 0 : -1;
      }),
    );
  };

  private readonly _onKeyDown = (e: KeyboardEvent): void => {
    if (this._host.disabled) return;
    const pos = this._findPos(e.target);
    if (!pos) return;

    const grid = this._getGrid();
    let handled = true;

    switch (e.key) {
      case 'ArrowRight':
        this._moveFocus(pos.row, pos.col + 1);
        break;
      case 'ArrowLeft':
        this._moveFocus(pos.row, pos.col - 1);
        break;
      case 'ArrowDown':
        this._moveFocus(pos.row + 1, pos.col);
        break;
      case 'ArrowUp':
        this._moveFocus(pos.row - 1, pos.col);
        break;
      case 'Home':
        this._moveFocus(e.ctrlKey ? 0 : pos.row, 0);
        break;
      case 'End': {
        const targetRow = e.ctrlKey ? grid.length - 1 : pos.row;
        this._moveFocus(targetRow, (grid[targetRow]?.length ?? 1) - 1);
        break;
      }
      default:
        handled = false;
    }

    if (handled) e.preventDefault();
  };
}
